import React, { useState, useEffect, useCallback } from 'react';
import { Plus, LogIn, Trash2, Layers } from 'lucide-react';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import ConfirmModal from '../components/UI/ConfirmModal';
import TemplateRolePrompt from '../components/TemplateRolePrompt';

const INDUSTRIES = ['Technology', 'Manufacturing', 'Finance', 'Healthcare', 'Retail', 'Other'];

const AVATAR_COLORS = [
  'bg-cyan-400 text-gray-950',
  'bg-purple-400 text-gray-950',
  'bg-emerald-400 text-gray-950',
  'bg-amber-400 text-gray-950',
  'bg-blue-400 text-gray-950',
];

// listWorkspaces returns r.data directly — may be a flat array or { tenants: [...] }
const normalizeWorkspaces = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.tenants)) return data.tenants;
  return [];
};

const OrgMembersWorkspaces = ({ activeTenantId }) => {
  const { switchOrg } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('Technology');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, ws: null });
  const [deleteError, setDeleteError] = useState(null);
  const [newOrgId, setNewOrgId] = useState(null);
  const [adminRoleId, setAdminRoleId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orgApi.listWorkspaces();
      const normalized = normalizeWorkspaces(data);
      setWorkspaces(normalized);
      return normalized;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);
    try {
      const res = await orgApi.createOrg(orgName, industry);
      setOrgName('');
      setIndustry('Technology');
      const newTenantId = res?.tenant_id || res?.id;
      if (newTenantId) {
        const rolesRes = await orgApi.getRoles(newTenantId);
        const roles = rolesRes?.roles || [];
        const admin = roles.find(r => r.is_system);
        setNewOrgId(newTenantId);
        setAdminRoleId(admin?.id || null);
      } else {
        await load();
      }
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Failed to create organization.');
    } finally {
      setCreating(false);
    }
  };

  const handleTemplateComplete = async (selected) => {
    if (newOrgId) {
      await switchOrg(newOrgId);
    }
    setNewOrgId(null);
    setAdminRoleId(null);
  };

  const handleTemplateSkip = async () => {
    if (newOrgId) {
      await switchOrg(newOrgId);
    }
    setNewOrgId(null);
    setAdminRoleId(null);
  };

  const handleSwitch = async (ws) => {
    await switchOrg(ws.id);
  };

  const handleDelete = async () => {
    const ws = deleteModal.ws;
    if (!ws) return;
    try {
      await orgApi.deleteOrg(ws.id);
      setDeleteModal({ open: false, ws: null });
      const remaining = await load();
      if (ws.id === activeTenantId) {
        const next = remaining?.[0];
        if (next) {
          await switchOrg(next.id);
        } else {
          window.location.reload();
        }
      }
    } catch {
      setDeleteError('Failed to delete organization.');
      setDeleteModal({ open: false, ws: null });
    }
  };

  return (
    <div className="grid grid-cols-2 gap-5">
      {deleteError && (
        <div className="col-span-2 bg-red-400/10 border border-red-400/20 text-red-400 text-sm px-4 py-3 rounded-xl">
          {deleteError}
        </div>
      )}
      {/* Provision new org */}
      <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-5 h-fit">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Plus className="w-3.5 h-3.5 text-cyan-400" /> Provision Organization · ORG-M-01
        </p>
        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Organization Name</label>
            <input
              required
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="e.g. Client Alpha Sdn Bhd"
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Industry Sector</label>
            <select
              value={industry}
              onChange={e => setIndustry(e.target.value)}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
            >
              {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
            </select>
          </div>
          {createError && <p className="text-xs text-red-400">{createError}</p>}
          <button
            type="submit"
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50 mt-1"
          >
            <Plus className="w-4 h-4" />
            {creating ? 'Creating…' : 'Create Tenant Organization'}
          </button>
        </form>
        <p className="text-xs text-gray-600 mt-4 leading-relaxed">
          You are automatically assigned Org Admin. Each organization is fully isolated — separate assets, events, members, and audit logs.
        </p>
      </div>

      {/* Workspace list */}
      <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-purple-400" /> Your Workspaces · ORG-M-04
        </p>
        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading…</div>
        ) : workspaces.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No workspaces yet.</div>
        ) : (
          <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
            {workspaces.map((ws, i) => {
              const isActive = ws.id === activeTenantId;
              return (
                <div
                  key={ws.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    isActive ? 'bg-cyan-400/5 border-cyan-400/30' : 'bg-gray-950 border-gray-700/50 hover:border-gray-600'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-base flex-shrink-0 ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                    {(ws.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">{ws.name}</span>
                      {isActive && (
                        <span className="text-xs px-1.5 py-0.5 bg-cyan-400 text-gray-950 rounded-full font-bold">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{ws.industry || 'Organization'}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!isActive && (
                      <button
                        onClick={() => handleSwitch(ws)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-lg hover:bg-cyan-400/20 transition-colors"
                      >
                        <LogIn className="w-3 h-3" /> Switch
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteModal({ open: true, ws })}
                      className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 border border-gray-700 hover:border-red-400/30 rounded-lg transition-colors"
                      title="Delete organization"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete Organization"
        message={`Permanently delete "${deleteModal.ws?.name}" and all its data? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ open: false, ws: null })}
      />

      {newOrgId && (
        <TemplateRolePrompt
          tenantId={newOrgId}
          adminRoleId={adminRoleId}
          onComplete={handleTemplateComplete}
          onSkip={handleTemplateSkip}
        />
      )}
    </div>
  );
};

export default OrgMembersWorkspaces;
